import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../lib/auth/AuthProvider';
import {
  formatInviteCode,
  isValidInviteCodeFormat,
  getInviteCodeErrorMessage,
  getAndClearInviteCodeRedirectPath,
  getPostInviteCodeVerificationPath
} from '../../utils/inviteCodeUtils';
import { useSetAtom } from 'jotai';
import { verifyInviteCodeAtom } from '../../atoms/v3/inviteCodeAtoms';

/**
 * 邀请码验证页面
 * 全屏显示，iOS 风格设计
 */
export const InviteCode: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showLogoutOption, setShowLogoutOption] = useState(false);

  // 获取邀请码验证函数
  const verifyInviteCode = useSetAtom(verifyInviteCodeAtom);

  // 如果用户未登录，重定向到登录页面
  useEffect(() => {
    if (!user) {
      navigate('/v3/login', { replace: true });
    }
  }, [user, navigate]);

  // 处理输入变化
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const formatted = formatInviteCode(input);
    setCode(formatted);
    
    // 清除错误信息
    if (errorMessage) {
      setErrorMessage(null);
    }
  };

  // 处理提交
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!code.trim()) {
      setErrorMessage('请输入邀请码');
      return;
    }

    if (!isValidInviteCodeFormat(code)) {
      setErrorMessage('邀请码格式不正确');
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
    setShowLogoutOption(false);

    try {
      const result = await verifyInviteCode(code);

      if (result.success) {
        // 验证成功，获取重定向路径并跳转
        const redirectPath = getAndClearInviteCodeRedirectPath();
        const targetPath = getPostInviteCodeVerificationPath(redirectPath);

        console.log('[InviteCode] Verification successful, redirecting to:', targetPath);
        navigate(targetPath, { replace: true });
      } else {
        const errorMsg = getInviteCodeErrorMessage(result.message);
        setErrorMessage(errorMsg);

        // 如果是网络连接异常或认证相关错误，显示登出选项
        if (result.message.includes('网络') ||
            result.message.includes('连接') ||
            result.message.includes('异常') ||
            result.message.includes('认证') ||
            result.message.includes('未授权') ||
            result.message.includes('401')) {
          setShowLogoutOption(true);
        }
      }
    } catch (error) {
      console.error('[InviteCode] Verification error:', error);
      setErrorMessage('验证过程中发生错误，请重试');
      setShowLogoutOption(true);
    } finally {
      setIsLoading(false);
    }
  };

  // 处理登出
  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/v3/login', { replace: true });
    } catch (error) {
      console.error('[InviteCode] Logout error:', error);
    }
  };

  // 如果用户未登录，显示加载状态
  if (!user) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-ios-gray6 dark:bg-ios-dark-gray6">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-ios-blue dark:border-ios-dark-blue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-ios-blue dark:text-ios-dark-blue">正在验证登录状态...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen bg-ios-gray6 dark:bg-ios-dark-gray6 font-ios flex flex-col">
      {/* 主内容区域 */}
      <div className="flex-1 flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-md"
        >
          {/* 标题区域 */}
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">🎫</div>
            <h1 className="text-ios-title1 font-semibold text-ios-label dark:text-ios-dark-label mb-2">
              输入邀请码
            </h1>
            <p className="text-ios-body text-ios-secondary-label dark:text-ios-dark-secondary-label">
              请输入您的邀请码以继续使用 Tempo
            </p>
          </div>

          {/* 表单 */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 邀请码输入框 */}
            <div>
              <label 
                htmlFor="inviteCode" 
                className="block text-ios-footnote font-medium text-ios-label dark:text-ios-dark-label mb-2"
              >
                邀请码
              </label>
              <input
                id="inviteCode"
                type="text"
                value={code}
                onChange={handleInputChange}
                placeholder="请输入邀请码"
                className={`
                  w-full px-4 py-3 text-ios-body
                  bg-white dark:bg-ios-dark-gray5
                  border border-ios-separator dark:border-ios-dark-separator
                  rounded-ios-lg
                  text-ios-label dark:text-ios-dark-label
                  placeholder-ios-placeholder dark:placeholder-ios-dark-placeholder
                  focus:outline-none focus:ring-2 focus:ring-ios-blue focus:border-transparent
                  transition-colors duration-200
                  ${errorMessage ? 'border-ios-red focus:ring-ios-red' : ''}
                `}
                disabled={isLoading}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
              />
              
              {/* 错误信息 */}
              {errorMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-2"
                >
                  <p className="text-ios-footnote text-ios-red dark:text-ios-dark-red">
                    {errorMessage}
                  </p>

                  {/* 网络错误时显示登出选项 */}
                  {showLogoutOption && (
                    <div className="mt-3 p-3 bg-ios-gray5 dark:bg-ios-dark-gray4 rounded-ios-md">
                      <p className="text-ios-caption1 text-ios-secondary-label dark:text-ios-dark-secondary-label mb-2">
                        如果网络连接异常，请检查网络并重试，或者登出后重新登录
                      </p>
                      <button
                        onClick={handleLogout}
                        className="text-ios-caption1 text-ios-blue dark:text-ios-dark-blue font-medium hover:underline"
                      >
                        登出并重新登录
                      </button>
                    </div>
                  )}
                </motion.div>
              )}
            </div>

            {/* 提交按钮 */}
            <button
              type="submit"
              disabled={isLoading || !code.trim()}
              className={`
                w-full py-3 px-4 text-ios-body font-medium rounded-ios-lg
                transition-all duration-200
                ${
                  isLoading || !code.trim()
                    ? 'bg-ios-gray3 dark:bg-ios-dark-gray3 text-ios-tertiary-label dark:text-ios-dark-tertiary-label cursor-not-allowed'
                    : 'bg-ios-blue text-white hover:bg-ios-blue-dark active:scale-95'
                }
              `}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  验证中...
                </div>
              ) : (
                '验证邀请码'
              )}
            </button>
          </form>

          {/* 帮助信息 */}
          <div className="mt-8 p-4 bg-ios-gray5 dark:bg-ios-dark-gray4 rounded-ios-lg">
            <div className="flex items-start space-x-3">
              <div className="text-ios-blue text-xl">💡</div>
              <div className="flex-1">
                <h3 className="text-ios-subhead font-medium text-ios-label dark:text-ios-dark-label mb-1">
                  如何获取邀请码？
                </h3>
                <p className="text-ios-footnote text-ios-secondary-label dark:text-ios-dark-secondary-label">
                  请加入小红书群或<a href="https://www.xiaohongshu.com/user/profile/664d4fd30000000003031bdc" className='text-ios-blue dark:text-ios-dark-blue'>私信开发者</a>并填写内测问卷获取邀请码。如果您已经填写过问卷，请联系管理员获取您的邀请码。
                </p>
              </div>
            </div>
          </div>

          {/* 账号切换选项 */}
          <div className="mt-4 text-center">
            <button
              onClick={handleLogout}
              className="text-ios-caption1 text-ios-secondary-label dark:text-ios-dark-secondary-label hover:text-ios-blue dark:hover:text-ios-dark-blue transition-colors"
            >
              登录错了账号或邀请码验证失败？试试登出重新登录
            </button>
          </div>
        </motion.div>
      </div>

      {/* 底部信息 */}
      <div className="px-6 pb-8">
        <p className="text-center text-ios-caption1 text-ios-tertiary-label dark:text-ios-dark-tertiary-label">
          Tempo 内测版本 · 仅限受邀用户使用
        </p>
      </div>
    </div>
  );
};

export default InviteCode;
